import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

import styles from './PostStory.module.css';

const cat_icon =
  'https://cdn.nasrollah.info/web_stories_38dp_000000_FILL0_wght400_GRAD0_opsz40.png';

const PostStory = () => {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedStories, setRelatedStories] = useState([]);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const videoRef = useRef(null);
  const audioRef = useRef(null);

  let lanuagePicked = localStorage.getItem('siteLanguage');
  const isFa = lanuagePicked === 'fa';
  let apiUrl = '';

  // --- Route / API Config for Stories ---
  const getAPIConfig = () => {
    return {
      function: 'getStoryById',   
      routePrefix: 'stories',
      category: 'story',
      displayName: {
        fa: 'داستان‌ها',
        ar: 'القصص'
      }
    };
  };

  const apiConfig = getAPIConfig();
  const helmetKey = `story-${slug}-${id}`;

  const getCategoryDisplayName = () => {
    return apiConfig.displayName[lanuagePicked] || apiConfig.displayName['fa'];
  };

  // --- iOS Specific Helpers ---
  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  const isAnyIOSDevice = isIOS();

  const handleVideoError = (error, videoElement) => {
    console.error('Story video playback error:', error);
    setVideoError(true);
    setIsMediaLoading(false);

    if (isAnyIOSDevice && retryCount < 5) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setVideoError(false);
        setIsMediaLoading(true);
        if (videoElement) {
          videoElement.load();
          setTimeout(() => {
            if (videoElement.readyState >= 2) {
              videoElement.currentTime = 0;
            }
          }, 500);
        }
      }, 1500);
    }
  };

  const handleVideoLoadStart = () => {
    setIsMediaLoading(true);
    setVideoError(false);
  };

  const handleVideoLoadedData = (videoElement) => {
    setIsMediaLoading(false);
    setVideoError(false);
    if (isAnyIOSDevice && videoElement) {
      videoElement.currentTime = 0;
    }
  };

  const prepareVideoForIOS = (videoElement) => {
    if (!videoElement || !isAnyIOSDevice) return;

    videoElement.currentTime = 0;

    videoElement.addEventListener('loadstart', () =>
      console.log('Story video load started')
    );
    videoElement.addEventListener('loadedmetadata', () => {
      console.log('Story video metadata loaded');
      if (videoElement.duration > 0) {
        videoElement.currentTime = 0.1;
        setTimeout(() => {
          videoElement.currentTime = 0;
        }, 100);
      }
    });
    videoElement.addEventListener('canplay', () => {
      console.log('Story video can play');
      setIsMediaLoading(false);
      setVideoError(false);
    });
  };

  // --- Date Formatting (Persian / Arabic) ---
  const formatPersianDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(dateString));
    } catch (error) {
      return dateString;
    }
  };

  const formatArabicDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Intl.DateTimeFormat('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(dateString));
    } catch (error) {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    return isFa ? formatPersianDate(dateString) : formatArabicDate(dateString);
  };

  // --- Fetch Story ---
  useEffect(() => {
    const fetchStory = async () => {
      setLoading(true);
      setError(null);

      try {
        if (isFa) {
          apiUrl = `https://api.nasrollah.info/fa/?function=${apiConfig.function}&params=${id}`;
        } else {
          apiUrl = `https://api.nasrollah.info/ar/?function=${apiConfig.function}&params=${id}`;
        }

        const response = await axios.get(apiUrl, {
          params: { slug: slug, include_related: true },
          timeout: 15000,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });

        const storyData = response.data;

        if (storyData.slug_post !== slug) {
          console.warn('URL slug does not match story slug');
        }

        if (storyData.name_category?.toLowerCase() !== apiConfig.category) {
          console.warn(
            `Category mismatch: expected ${apiConfig.category}, got ${storyData.name_category}`
          );
        }

        setStory(storyData);
        if (storyData.related_posts) {
          setRelatedStories(storyData.related_posts.slice(0, 4));
        }
      } catch (err) {
        console.error('❌ Error fetching story:', err);
        const errorMsg = isFa
          ? 'داستان یافت نشد'
          : 'لم يتم العثور على القصة';
        const genericError = isFa
          ? 'خطا در بارگذاری داستان'
          : 'خطأ في تحميل القصة';
        setError(
          err.response?.status === 404
            ? errorMsg
            : genericError
        );
      } finally {
        setLoading(false);
      }
    };

    if (slug && id) {
      fetchStory();
    } else {
      setError(
        isFa ? 'پارامترهای URL نامعتبر است' : 'معلمات URL غير صالحة'
      );
      setLoading(false);
    }
  }, [slug, id, isFa, apiConfig.function]);

  // --- Prepare Video for iOS when Story Loaded ---
  useEffect(() => {
    if (story && story.media_type === 'video') {
      setTimeout(() => {
        if (videoRef.current) {
          prepareVideoForIOS(videoRef.current);
        }
      }, 100);
    }
  }, [story]);

  // --- Document Title & Meta Tags ---
  useEffect(() => {
    if (story?.title_post) {
      document.title = `${story.title_post} - ${getCategoryDisplayName()}`;
    }
  }, [story]);

  useEffect(() => {
    if (story) {
      setTimeout(() => {
        const setMeta = (name, content, isProperty = false) => {
          const attr = isProperty ? 'property' : 'name';
          const selector = `meta[${attr}="${name}"]`;
          let meta = document.querySelector(selector);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute(attr, name);
            document.head.appendChild(meta);
          }
          if (content) meta.setAttribute('content', content);
        };

        setMeta(
          'description',
          story.meta_description_post ||
            story.keywords_post ||
            story.title_post
        );
        setMeta('keywords', story.keywords_post);
        setMeta('author', story.author_post);
        setMeta('og:title', story.title_post, true);
        setMeta(
          'og:description',
          story.meta_description_post || story.keywords_post,
          true
        );
      }, 100);
    }
  }, [story]);

  // --- Structured Data ---
  const generateStructuredData = () => {
    if (!story) return {};

    const baseData = {
      '@context': 'https://schema.org',
      '@type':
        story.media_type === 'video'
          ? 'VideoObject'
          : 'Article',
      headline: story.title_post,
      description:
        story.meta_description_post || story.keywords_post,
      datePublished: story.create_time_post,
      dateModified:
        story.original_time_post || story.create_time_post,
      author: {
        '@type': 'Person',
        name: story.author_post
      },
      url: `${window.location.origin}/${apiConfig.routePrefix}/${slug}/${id}`
    };

    if (story.featured_media_post) {
      if (story.media_type === 'video') {
        baseData.contentUrl = story.featured_media_post;
        baseData.embedUrl = story.featured_media_post;
        baseData.thumbnailUrl = story.featured_post;
      } else {
        baseData.image = story.featured_media_post;
      }
    }

    return baseData;
  };

  const commonHelmet = (
    <Helmet key={helmetKey}>
      <title>
        {story?.title_post
          ? `${story.title_post} - ${getCategoryDisplayName()}`
          : isFa
            ? 'در حال بارگذاری...'
            : 'جار التحميل...'}
      </title>
      {story?.meta_description_post && (
        <meta
          name="description"
          content={story.meta_description_post}
        />
      )}
      {story?.keywords_post && (
        <meta
          name="keywords"
          content={story.keywords_post}
        />
      )}
      {story?.author_post && (
        <meta
          name="author"
          content={story.author_post}
        />
      )}
      {story?.title_post && (
        <meta property="og:title" content={story.title_post} />
      )}
      {(story?.meta_description_post ||
        story?.keywords_post) && (
        <meta
          property="og:description"
          content={
            story.meta_description_post ||
            story.keywords_post
          }
        />
      )}
      <meta property="og:type" content="article" />
      <meta
        property="og:url"
        content={`${window.location.origin}/${apiConfig.routePrefix}/${slug}/${id}`}
      />
      {story?.featured_media_post && (
        <meta
          property="og:image"
          content={story.featured_media_post}
        />
      )}
      {story?.create_time_post && (
        <meta
          property="article:published_time"
          content={story.create_time_post}
        />
      )}
      {story?.author_post && (
        <meta
          property="article:author"
          content={story.author_post}
        />
      )}
      <link
        rel="canonical"
        href={`${window.location.origin}/${apiConfig.routePrefix}/${slug}/${id}`}
      />
      {story && (
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData())}
        </script>
      )}
    </Helmet>
  );

  // --- Loading, Error, Not Found States ---
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p className={styles.loadingText}>
          {isFa
            ? `در حال بارگذاری ${getCategoryDisplayName()}...`
            : `جاري تحميل ${getCategoryDisplayName()}...`}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>
            {isFa
              ? `خطا در بارگذاری - ${error}`
              : `خطأ في التحميل - ${error}`}
          </title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h1 className={styles.errorTitle}>
            {isFa
              ? `خطا در بارگذاری ${getCategoryDisplayName()}`
              : `خطأ في تحميل ${getCategoryDisplayName()}`}
          </h1>
          <p className={styles.errorMessage}>{error}</p>
          <div className={styles.errorActions}>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              {isFa ? 'بازگشت' : 'رجوع'}
            </button>
            <button
              onClick={() => window.location.reload()}
              className={styles.retryButton}
            >
              {isFa ? 'تلاش مجدد' : 'إعادة المحاولة'}
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!story) {
    return (
      <>
        <Helmet>
          <title>
            {isFa
              ? `${getCategoryDisplayName()} یافت نشد`
              : `لم يتم العثور على ${getCategoryDisplayName()}`}
          </title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className={styles.notFoundContainer}>
          <div className={styles.notFoundIcon}>📄</div>
          <h1 className={styles.notFoundTitle}>
            {isFa
              ? `${getCategoryDisplayName()} یافت نشد`
              : `لم يتم العثور على ${getCategoryDisplayName()}`}
          </h1>
          <p className={styles.notFoundMessage}>
            {isFa
              ? `متأسفانه ${getCategoryDisplayName()} درخواستی شما پیدا نشد.`
              : `للأسف، لم يتم العثور على ${getCategoryDisplayName()} المطلوب.`}
          </p>
          <button
            onClick={() => navigate(-1)}
            className={styles.backButton}
          >
            {isFa ? 'بازگشت' : 'رجوع'}
          </button>
        </div>
      </>
    );
  }

  // --- Media Type Helpers ---
  const getStoryMediaType = () => {
    const mediaTypeFromPost = story?.media_type?.toLowerCase?.();

    if (mediaTypeFromPost === 'video' || mediaTypeFromPost === 'audio' || mediaTypeFromPost === 'image') {
      return mediaTypeFromPost;
    }

    const mediaUrl = story?.featured_media_post || '';
    const ext = mediaUrl.split('?')[0].split('.').pop()?.toLowerCase();

    if (['mp4', 'webm', 'ogg', 'mov'].includes(ext)) return 'video';
    if (['mp3', 'mpeg', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) return 'audio';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg'].includes(ext)) return 'image';

    return 'image';
  };

  const getPremiumIcon = (type) => {
  const icons = {
    image: 'auto_awesome',
    audio: 'graphic_eq',
    video: 'movie',
    production: 'movie_edit',
    story: 'stars',
    related: 'linked_services',
    date: 'calendar_month',
    author: 'person',
    update: 'update',
    error: 'error',
    notFound: 'search_off',
    published: 'verified',
    pending: 'schedule',
    back: 'arrow_back',
    retry: 'refresh'
  };
  return icons[type] || icons.story;
};

const PremiumIcon = ({ type, className = '', filled = false, label }) => {
  return (
    <span
      className={`material-symbols-rounded ${className}`}
      style={{
        fontVariationSettings: `
          'FILL' ${filled ? 1 : 0},
          'wght' ${filled ? 600 : 500},
          'GRAD' 0,
          'opsz' 24
        `
      }}
      aria-hidden={label ? undefined : 'true'}
      aria-label={label}
      role={label ? 'img' : undefined}
    >
      {getPremiumIcon(type)}
    </span>
  );
};


  const renderMedia = () => {
    const mediaType = getStoryMediaType();

    if (!story.featured_media_post) {
      return (
        <div className={styles.defaultMediaContainer}>
          <div className={styles.defaultStoryIcon}>
            <span className={styles.defaultStoryIconSymbol}>{getPremiumIcon('story')}</span>
          </div>
        </div>
      );
    }

    const mediaLoadingOverlay =
      isMediaLoading && (
        <div className={styles.mediaLoadingOverlay}>
          <div className={styles.mediaLoadingSpinner}></div>
        </div>
      );

    switch (mediaType) {
      case 'video':
        return (
          <div className={styles.videoContainer}>
            <video
              ref={videoRef}
              controls
              poster={story.media_post || story.featured_post}
              className={styles.videoPlayer}
              controlsList="nodownload"
              preload="metadata"
              playsInline={true}
              webkit-playsinline="true"
              x5-playsinline="true"
              x5-video-player-type="h5"
              x5-video-player-fullscreen="true"
              onLoadStart={handleVideoLoadStart}
              onLoadedData={() => handleVideoLoadedData(videoRef.current)}
              onError={(e) => handleVideoError(e.target.error, videoRef.current)}
              autoPlay={false}
              muted={false}
            >
              <source src={story.featured_media_post} type="video/mp4" />
              <source src={story.featured_media_post.replace('.mp4', '.webm')} type="video/webm" />
              <source src={story.featured_media_post.replace('.mp4', '.ogg')} type="video/ogg" />
              {isFa ? 'مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.' : 'متصفحك لا يدعم تشغيل الفيديو.'}
            </video>
            {mediaLoadingOverlay}
          </div>
        );

      case 'audio':
        return (
          <div className={styles.audioContainer}>
            <div className={styles.audioPlayer}>
              <div className={styles.audioThumbnail}>
                <span className={styles.audioPremiumIcon}>{getPremiumIcon('audio')}</span>
              </div>
              <audio
                ref={audioRef}
                controls
                className={styles.audioElement}
                preload="metadata"
                controlsList="nodownload"
                onLoadStart={() => setIsMediaLoading(true)}
                onLoadedData={() => setIsMediaLoading(false)}
                onError={() => setIsMediaLoading(false)}
              >
                <source src={story.featured_media_post} type="audio/mpeg" />
                <source src={story.featured_media_post} type="audio/mp3" />
                <source src={story.featured_media_post.replace('.mp3', '.ogg')} type="audio/ogg" />
                <source src={story.featured_media_post.replace('.mp3', '.wav')} type="audio/wav" />
                {isFa ? 'مرورگر شما از پخش صوت پشتیبانی نمی‌کند.' : 'متصفحك لا يدعم تشغيل الصوت.'}
              </audio>
            </div>
            {mediaLoadingOverlay}
          </div>
        );

      case 'image':
      default:
        return (
          <div className={styles.imageContainer}>
            <img
              src={story.featured_media_post}
              alt={story.title_post}
              className={styles.storyImage}
              loading="lazy"
              onLoad={() => setIsMediaLoading(false)}
              onError={() => setIsMediaLoading(false)}
            />
            {mediaLoadingOverlay}
          </div>
        );
    }
  };

  const renderRelatedStories = () => {
    if (relatedStories.length === 0) return null;

    return (
      <section className={styles.relatedStoriesSection}>
        <h2 className={styles.relatedStoriesTitle}>
          <span className={styles.relatedStoriesTitleIcon}>{getPremiumIcon('related')}</span>
          {isFa ? 'داستان‌های مرتبط' : 'قصص ذات صلة'}
        </h2>

        <div className={styles.relatedStoriesGrid}>
          {relatedStories.map((relatedStory, index) => {
            const relatedMediaUrl = relatedStory.featured_media_post || '';
            const relatedExt = relatedMediaUrl.split('?')[0].split('.').pop()?.toLowerCase();
            const relatedType = ['mp4', 'webm', 'ogg', 'mov'].includes(relatedExt)
              ? 'video'
              : ['mp3', 'mpeg', 'wav', 'm4a', 'aac'].includes(relatedExt)
                ? 'audio'
                : 'image';

            return (
              <Link
                key={`${relatedStory.id_post}-${index}`}
                to={`/${apiConfig.routePrefix}/${relatedStory.slug_post}/${relatedStory.id_post}`}
                className={styles.relatedStoryCard}
                title={relatedStory.title_post}
              >
                <div className={styles.relatedStoryMedia}>
                  {relatedStory.featured_media_post ? (
                    relatedType === 'video' ? (
                      <div className={styles.relatedStoryVideoThumb}>
                        <span className={styles.relatedStoryMediaIcon}>{getPremiumIcon('video')}</span>
                      </div>
                    ) : relatedType === 'audio' ? (
                      <div className={styles.relatedStoryAudioThumb}>
                        <span className={styles.relatedStoryMediaIcon}>{getPremiumIcon('audio')}</span>
                      </div>
                    ) : (
                      <img
                        src={relatedStory.featured_media_post}
                        alt={relatedStory.title_post}
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = '';
                        }}
                      />
                    )
                  ) : (
                    <div className={styles.relatedStoryFallback}>
                      <span className={styles.relatedStoryMediaIcon}>{getPremiumIcon('story')}</span>
                    </div>
                  )}
                </div>

                <div className={styles.relatedStoryContent}>
                  <h3 className={styles.relatedStoryTitle}>{relatedStory.title_post}</h3>
                  <p className={styles.relatedStoryDate}>{formatDate(relatedStory.create_time_post)}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    );
  };

  // --- Main Render ---
  if (isFa) {
    return (
      <>
        {commonHelmet}
        <article className={`${styles.postContainer} ${styles.isPersian}`}>
          <div className={styles.postWrapper}>
            <nav className={styles.breadcrumb} aria-label="مسیر صفحه">
              <Link to="/home" className={styles.breadcrumbLink}>
                صفحه اصلی
              </Link>
              <span className={styles.breadcrumbSeparator}>/</span>
              <Link to={`/${apiConfig.routePrefix}`} className={styles.breadcrumbLink}>
                {getCategoryDisplayName()}
              </Link>
              <span className={styles.breadcrumbSeparator}>/</span>
              <span className={styles.breadcrumbCurrent}>{story.title_post}</span>
            </nav>

            <div className={styles.categoryBadge}>
  <span className={styles.categoryIcon}>
    <img
      src={cat_icon}
      alt=""
      className={styles.categoryIconImg}
      width="20"
      height="20"
    />
  </span>
  <span className={styles.categoryText}>{getCategoryDisplayName()}</span>
</div>

            <header className={styles.postHeader}>
              <h1 className={styles.postTitle}>{story.title_post}</h1>
            </header>

            <div className={styles.postMeta}>
              <div className={styles.metaItem}>
                <span className={styles.metaIcon}>{getPremiumIcon('date')}</span>
                <time dateTime={story.create_time_post} className={styles.metaText}>
                  {formatDate(story.create_time_post)}
                </time>
              </div>

              <div className={styles.metaSeparator}>•</div>

              <div className={styles.metaItem}>
                <span className={styles.metaIcon}>{getPremiumIcon('author')}</span>
                <span className={styles.metaText}>{story.author_post}</span>
              </div>

              {story.original_time_post && story.original_time_post !== story.create_time_post && (
                <>
                  <div className={styles.metaSeparator}>•</div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaIcon}>{getPremiumIcon('update')}</span>
                    <time
                      dateTime={story.original_time_post}
                      className={styles.metaText}
                      title="تاریخ بروزرسانی"
                    >
                      {formatDate(story.original_time_post)}
                    </time>
                  </div>
                </>
              )}
            </div>

            <div className={styles.mediaSection}>{renderMedia()}</div>

            <div className={styles.postContent}>
              {story.html_post ? (
                <div
                  className={styles.contentHtml}
                  dangerouslySetInnerHTML={{ __html: story.html_post }}
                />
              ) : (
                <div className={styles.contentText}>
                  {story.keywords_post || story.meta_description_post}
                </div>
              )}
            </div>

            <footer className={styles.postFooter}>
  <div className={styles.postStatus}>
    <span className={styles.statusIcon}>
      <PremiumIcon
        type={story.status_post === 'published' ? 'published' : 'pending'}
        filled={story.status_post === 'published'}
      />
    </span>
    <span className={styles.statusText}>
      {story.status_post === 'published' ? 'منتشر شده' : 'در انتظار انتشار'}
    </span>
  </div>

  <div className={styles.navigationActions}>
    <Link
      to={`/${apiConfig.routePrefix}`}
      className={styles.backToListButton}
      title={`بازگشت به لیست ${getCategoryDisplayName()}`}
    >
      <PremiumIcon type="back" />
      <span>بازگشت به لیست</span>
    </Link>
  </div>
</footer>
          </div>

          {renderRelatedStories()}
        </article>
      </>
    );
  } else {
    return (
      <>
        {commonHelmet}
        <article className={`${styles.postContainer} ${styles.isArabic}`}>
          <div className={styles.postWrapper}>
            <nav className={styles.breadcrumb} aria-label="مسار الصفحة">
              <Link to="/home" className={styles.breadcrumbLink}>
                الرئيسية
              </Link>
              <span className={styles.breadcrumbSeparator}>/</span>
              <Link to={`/${apiConfig.routePrefix}`} className={styles.breadcrumbLink}>
                {getCategoryDisplayName()}
              </Link>
              <span className={styles.breadcrumbSeparator}>/</span>
              <span className={styles.breadcrumbCurrent}>{story.title_post}</span>
            </nav>

            <div className={styles.categoryBadge}>
  <span className={styles.categoryIcon}>
    <img
      src={cat_icon}
      alt=""
      className={styles.categoryIconImg}
      width="20"
      height="20"
    />
  </span>
  <span className={styles.categoryText}>{getCategoryDisplayName()}</span>
</div>

            <header className={styles.postHeader}>
              <h1 className={styles.postTitle}>{story.title_post}</h1>
            </header>

            <div className={styles.postMeta}>
              <div className={styles.metaItem}>
                <span className={styles.metaIcon}>{getPremiumIcon('date')}</span>
                <time dateTime={story.create_time_post} className={styles.metaText}>
                  {formatDate(story.create_time_post)}
                </time>
              </div>

              <div className={styles.metaSeparator}>•</div>

              <div className={styles.metaItem}>
                <span className={styles.metaIcon}>{getPremiumIcon('author')}</span>
                <span className={styles.metaText}>{story.author_post}</span>
              </div>

              {story.original_time_post && story.original_time_post !== story.create_time_post && (
                <>
                  <div className={styles.metaSeparator}>•</div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaIcon}>{getPremiumIcon('update')}</span>
                    <time
                      dateTime={story.original_time_post}
                      className={styles.metaText}
                      title="تاريخ التحديث"
                    >
                      {formatDate(story.original_time_post)}
                    </time>
                  </div>
                </>
              )}
            </div>

            <div className={styles.mediaSection}>{renderMedia()}</div>

            <div className={styles.postContent}>
              {story.html_post ? (
                <div
                  className={styles.contentHtml}
                  dangerouslySetInnerHTML={{ __html: story.html_post }}
                />
              ) : (
                <div className={styles.contentText}>
                  {story.keywords_post || story.meta_description_post}
                </div>
              )}
            </div>

            <footer className={styles.postFooter}>
  <div className={styles.postStatus}>
    <span className={styles.statusIcon}>
      <PremiumIcon
        type={story.status_post === 'published' ? 'published' : 'pending'}
        filled={story.status_post === 'published'}
      />
    </span>
    <span className={styles.statusText}>
      {story.status_post === 'published' ? 'منشور' : 'منتظر النشر'}
    </span>
  </div>

  <div className={styles.navigationActions}>
    <Link
      to={`/${apiConfig.routePrefix}`}
      className={styles.backToListButton}
      title={`العودة إلى قائمة ${getCategoryDisplayName()}`}
    >
      <PremiumIcon type="back" />
      <span>العودة إلى القائمة</span>
    </Link>
  </div>
</footer>
          </div>

          {renderRelatedStories()}
        </article>
      </>
    );
  }

};

export default PostStory;
